import { on } from './store-events.js';

/**
 * Upsell Provider - Toggle visibility based on cart state
 *
 * Both collections are pre-rendered by Liquid.
 * JS toggles visibility based on cart contents.
 */

function getCartHandles() {
  try {
    const state = window.store?.cartState;
    if (state?.items) {
      return state.items.map((it) => (it.product?.handle || it.handle || '').toLowerCase()).filter(Boolean);
    }
  } catch (_) {}
  return null;
}

async function fetchCartHandles() {
  const cached = getCartHandles();
  if (cached) return cached;

  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    if (cart?.items) {
      return cart.items.map((it) => (it.product?.handle || it.handle || '').toLowerCase()).filter(Boolean);
    }
  } catch (_) {}

  return [];
}

async function updateProvider(root) {
  const cartHandles = await fetchCartHandles();
  const cartSet = new Set(cartHandles);
  const maxItems = parseInt(root.getAttribute('data-max') || '2', 10);
  const primaryCollectionHandle = root.getAttribute('data-primary') || '';

  // Get all upsell items
  const primaryItems = Array.from(root.querySelectorAll('[data-collection="primary"]'));
  const secondaryItems = Array.from(root.querySelectorAll('[data-collection="secondary"]'));

  // Build set of primary collection handles from pre-rendered items
  const primaryHandles = new Set(
    primaryItems.map((el) => (el.getAttribute('data-handle') || '').toLowerCase()).filter(Boolean)
  );

  // Check if cart has any item from primary collection
  const hasPrimaryInCart = cartHandles.some((h) => primaryHandles.has(h));

  // Determine which collection to show
  const activeRule = hasPrimaryInCart && secondaryItems.length > 0 ? 'secondary' : 'primary';
  const activeItems = activeRule === 'secondary' ? secondaryItems : primaryItems;

  // Hide all items first
  [...primaryItems, ...secondaryItems].forEach((el) => el.setAttribute('hidden', ''));

  // Show items from active collection (up to max, excluding items in cart)
  let shown = 0;
  for (const el of activeItems) {
    if (shown >= maxItems) break;

    const handle = (el.getAttribute('data-handle') || '').toLowerCase();
    if (handle && cartSet.has(handle)) continue;

    el.removeAttribute('hidden');
    shown++;
  }

  // Toggle visibility of divider, heading, and entire provider
  const divider = root.querySelector('[data-upsell-divider]');
  const heading = root.querySelector('[data-upsell-heading]');

  if (shown === 0) {
    // No items to show - hide everything
    root.setAttribute('hidden', '');
    if (divider) divider.setAttribute('hidden', '');
    if (heading) heading.setAttribute('hidden', '');
  } else {
    // Has items - show everything
    root.removeAttribute('hidden');
    if (divider) divider.removeAttribute('hidden');
    if (heading) heading.removeAttribute('hidden');
  }
}

function updateAllProviders() {
  const providers = document.querySelectorAll('store-upsell-provider');
  providers.forEach((el) => updateProvider(el));
}

// Register as custom element
class StoreUpsellProvider extends HTMLElement {
  connectedCallback() {
    // Update on connect to sync with current cart state
    updateProvider(this);
  }
}

try {
  if (!customElements.get('store-upsell-provider')) {
    customElements.define('store-upsell-provider', StoreUpsellProvider);
  }
} catch (_) {}

// Initial update
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateAllProviders, { once: true });
} else {
  updateAllProviders();
}

// Update on cart changes
on('store:cart:update', updateAllProviders);
on('store:cart:added', updateAllProviders);
